<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading">
        <v-container class="mb-4">
          <v-card class="primary">
            <v-row class="pb-1">
              <v-col class="text-center white--text">{{
                currentWeekDates
              }}</v-col>
              <v-col class="text-center white--text">{{ nextWeekDates }}</v-col>
            </v-row>
            <v-simple-table dense class="font-weight-light available-table">
              <template v-slot:default>
                <thead class="grey lighten-4">
                  <tr>
                    <th></th>
                    <th v-for="d in currentWeekDays" :key="d">{{ d }}</th>
                    <th>-</th>
                    <th v-for="d in nextWeekDays" :key="d">{{ d }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ $t('scheduler.morning') }} ({{ $t('general.before') }} {{ cleanerHalfDayHour }})</td>
                    <td
                      v-for="(day, index) in currentWeekAvailable"
                      :key="`current-${index}`"
                    >
                      <v-menu open-on-hover bottom offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            :class="{ ok: day.am }"
                            >{{ day.am || '-' }}</span
                          >
                        </template>
                        <div class="d-flex flex-column white pa-2">
                          <div
                            class="body-2"
                            v-for="(item, index) in day.am_cleaners"
                            :key="index"
                          >
                            {{ item }}
                          </div>
                        </div>
                      </v-menu>
                    </td>
                    <td />
                    <td
                      v-for="(day, index) in nextWeekAvailable"
                      :key="`next-${index}`"
                    >
                      <v-menu open-on-hover bottom offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            :class="{ ok: day.am }"
                            >{{ day.am || '-' }}</span
                          >
                        </template>
                        <div class="d-flex flex-column white pa-2">
                          <div
                            class="body-2"
                            v-for="(item, index) in day.am_cleaners"
                            :key="index"
                          >
                            {{ item }}
                          </div>
                        </div>
                      </v-menu>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('scheduler.afternoon') }}</td>
                    <td
                      v-for="(day, index) in currentWeekAvailable"
                      :key="`current-${index}`"
                    >
                      <v-menu open-on-hover bottom offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            :class="{ ok: day.pm }"
                            >{{ day.pm || '-' }}</span
                          >
                        </template>
                        <div class="d-flex flex-column white pa-2">
                          <div
                            class="body-2"
                            v-for="(item, index) in day.pm_cleaners"
                            :key="index"
                          >
                            {{ item }}
                          </div>
                        </div>
                      </v-menu>
                    </td>
                    <td />
                    <td
                      v-for="(day, index) in nextWeekAvailable"
                      :key="`next-${index}`"
                    >
                      <v-menu open-on-hover bottom offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            :class="{ ok: day.pm }"
                            >{{ day.pm || '-' }}</span
                          >
                        </template>
                        <div class="d-flex flex-column white pa-2">
                          <div
                            class="body-2"
                            v-for="(item, index) in day.pm_cleaners"
                            :key="index"
                          >
                            {{ item }}
                          </div>
                        </div>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-container>
        <filter-cleaners
          :default-search-params="searchParams"
          :is-component-loading="isRequestingNow"
          @filtering="onFilter"
          @download-excel="onDownloadExcel"
        ></filter-cleaners>
        <paginated-cleaner-schedules
          :is-component-loading="isRequestingNow"
          :paginated-object="paginatedObject"
          @input-schedule="debounceUpdateSchedule"
        ></paginated-cleaner-schedules>
        <pagination-listing
          :pagination-data="paginationData"
          @go-prev="changePage('-')"
          @go-next="changePage('+')"
        ></pagination-listing>
      </v-flex>
      <modal-loading :is-show="isTableLoading"></modal-loading>
      <!-- Edit Modal -->
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
import debounce from 'debounce';
import moment from 'moment';
/* constants */
import { PER_PAGE } from '@/constants/listing';
/* utils */
import { prepareValidationMessages } from '@/utils/index';
/* proxies */
import CleanerProxy from '@/proxies/Cleaner/CleanerProxy';
/* mixins */
import defaultListingMixin from '@/mixins/listing/defaultListingMixin';
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import paginationMixin from '@/mixins/listing/paginationMixin';
import downloadReport from '@/mixins/reports/downloadReport';
/* components */
import ModalLoading from '@/components/loaders/ModalLoading.vue';
import FilterCleaners from '@/components/listings/FilterCleaners.vue';
import PaginatedCleanerSchedules from '@/components/listings/PaginatedCleanerSchedules.vue';
import CleanerTransformer from '@/transformers/Cleaners/CleanerTransformer';
import PaginationListing from '@/components/listings/PaginationListing.vue';
import i18n from '@/i18n';

export default {
  name: 'CleanerSchedules',
  components: {
    ModalLoading,
    FilterCleaners,
    PaginationListing,
    PaginatedCleanerSchedules
  },
  mixins: [
    updatePageContentMixin,
    paginationMixin,
    defaultListingMixin,
    downloadReport
  ],
  data() {
    return {
      reportMethodName: 'cleanersListingReport',
      /* Page management */
      pageLoading: false,
      /* Create - Edit user */
      isLoadingUserDialog: false,
      errorsUserDialog: {},
      isShowUserDialog: false,
      currentEditableItem: {},
      /* Delete user */
      isShowDeleteDialog: false,
      /* Listing management */
      isTableLoading: false,
      fetchDataOnCreated: true,
      searchParams: {
        search: '',
        active: 1,
        role: null,
        region: null,
        page: {
          number: 1,
          size: PER_PAGE
        }
      },
      currentWeekAvailable: [],
      nextWeekAvailable: [],
      debounceUpdateSchedule: debounce(this.updateSchedule, 2000)
    };
  },
  computed: {
    cleanerHalfDayHour() {
      return process.env.VUE_APP_CLEANER_HALF_DAY_HOUR;
    },
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
    currentWeekDays() {
      const weekVar = moment()
        .locale(this.getCurrentLocale())
        .startOf('isoWeek');
      const weekArr = [];
      for (let i = 0; i < 7; i += 1) {
        weekArr.push(weekVar.format('dd DD/MM'));
        weekVar.add(1, 'd');
      }
      return weekArr;
    },
    nextWeekDays() {
      const weekVar = moment()
        .locale(this.getCurrentLocale())
        .add(1, 'w')
        .startOf('isoWeek');
      const weekArr = [];
      for (let i = 0; i < 7; i += 1) {
        weekArr.push(weekVar.format('dd DD/MM'));
        weekVar.add(1, 'd');
      }
      return weekArr;
    },
    currentWeekDates() {
      const now = moment()
        .locale(this.getCurrentLocale()).startOf('isoWeek');
      return `${i18n.t('general.from')} ${now.startOf('isoWeek').format('DD-MM')} ${i18n.t('general.to')} ${
        now.endOf('isoWeek').format('DD-MM')}`;
    },
    nextWeekDates() {
      const next = moment()
        .locale(this.getCurrentLocale())
        .startOf('isoWeek')
        .add(1, 'w');
      return `${i18n.t('general.from')} ${next.startOf('isoWeek').format('DD-MM')} ${i18n.t('general.to')} ${
        next.endOf('isoWeek').format('DD-MM')}`;
    }
  },
  methods: {
    getCurrentLocale() {
      return window.localStorage.language;
    },
    fetchData() {
      this.isRequestingNow = true;
      const client = new CleanerProxy();
      client
        .setParameters({ region: this.searchParams.region })
        .available()
        .then(response => {
          this.currentWeekAvailable = response.data.current_week;
          this.nextWeekAvailable = response.data.next_week;
        })
        .catch(error => {
          console.error(error);
        });

      client
        .setParameters(this.searchParams)
        .schedules()
        .then(response => {
          // eslint-disable-next-line no-param-reassign
          response.data.data.cleaners.data = CleanerTransformer.fetchCollection(
            response.data.data.cleaners.data
          );
          this.paginatedObject = response.data.data.cleaners;
        })
        .catch(error => {
          console.warn(error);
          this.paginatedObject = {};
        })
        .finally(() => {
          this.isTableLoading = false;
          this.isRequestingNow = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    /**
     * @see defaultListingMixin
     * @param newSearchParams
     * @returns {*}
     */
    filterSearchParamsBeforeSend(newSearchParams) {
      if (_.isString(newSearchParams.role)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.role = [newSearchParams.role];
      }
      if (_.isString(newSearchParams.region)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.region = [newSearchParams.region];
      }
      return newSearchParams;
    },
    updateSchedule(item) {
      this.isLoadingUserDialog = true;
      const client = new CleanerProxy();
      client
        .updateSchedules(item.uuid, {
          currentWeek: item.currentWeek,
          nextWeek: item.nextWeek
        })
        .then(() => {
          client
            .available()
            .then(response => {
              this.currentWeekAvailable = response.data.current_week;
              this.nextWeekAvailable = response.data.next_week;
            })
            .catch(error => {
              console.error(error);
            });
          this.$store.dispatch('page/displaySnackbar', {
            text: 'График клинера обновлен',
            color: 'success',
            timeout: 3000
          });
        })
        .catch(response => {
          console.error(response);
          this.errorsUserDialog = prepareValidationMessages(response.data.data);
        })
        .finally(() => {
          this.isLoadingUserDialog = false;
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.available-table {
  td,
  th {
    text-align: center !important;
  }
}
.ok {
  color: green;
  font-weight: 400;
}
</style>
